import { getNavigationV2AppReturnPath } from "@/components/navigation/navigation-v2-return-path";
import type { Environment } from "@/gen_src/proto/chalk/server/v1/environment_pb";
import { useRouteAndTeamInfo } from "@/utils/hooks/url/use-route-info";
import { Routes } from "@/utils/routes";
import React, { PropsWithChildren, createContext, useContext, useMemo } from "react";

/** An environment the project nav's links can point into. */
export interface NavLinkEnvironmentIds {
  projectId: string;
  environmentId: string;
}

export interface NavLinkEnvironment extends NavLinkEnvironmentIds {
  environment: Environment | undefined;
}

const ENVIRONMENT_ROOT_PATH_PATTERN = /^\/projects\/([^/?#]+)\/environments\/([^/?#]+)/;

/**
 * The environment a global settings page's nav points into, since its URL names none: the one the
 * reader was last in (`returnPath`) while the team still has it, otherwise the team's first.
 */
export const resolveGlobalNavEnvironment = (
  returnPath: string | undefined,
  projects: readonly { id: string; environments: readonly { id: string }[] }[],
): NavLinkEnvironmentIds | undefined => {
  const [, projectId, environmentId] =
    (returnPath == null ? null : ENVIRONMENT_ROOT_PATH_PATTERN.exec(returnPath)) ?? [];
  if (
    projectId != null &&
    environmentId != null &&
    projects.some(
      (project) =>
        project.id === projectId &&
        project.environments.some((environment) => environment.id === environmentId),
    )
  ) {
    return { projectId, environmentId };
  }

  const project = projects.find((candidate) => candidate.environments.length > 0);
  const environment = project?.environments[0];
  return project != null && environment != null
    ? { projectId: project.id, environmentId: environment.id }
    : undefined;
};

/**
 * Where "Back to app" leads from a global settings page: the environment page the reader was last
 * on (`returnPath`) while the team still has its environment, otherwise the root of the
 * environment the nav points into.
 */
export const resolveGlobalNavReturnHref = (
  returnPath: string | undefined,
  projects: readonly { id: string; environments: readonly { id: string }[] }[],
): string | undefined => {
  const environment = resolveGlobalNavEnvironment(returnPath, projects);
  if (environment == null) return undefined;

  const [, projectId, environmentId] =
    (returnPath == null ? null : ENVIRONMENT_ROOT_PATH_PATTERN.exec(returnPath)) ?? [];
  return returnPath != null &&
    projectId === environment.projectId &&
    environmentId === environment.environmentId
    ? returnPath
    : Routes.project.environment.root(environment.projectId, environment.environmentId);
};

/** `resolveGlobalNavReturnHref` for the signed-in team; undefined until the team loads. */
export const useGlobalNavReturnHref = (): string | undefined => {
  const { team } = useRouteAndTeamInfo();
  const returnPath = getNavigationV2AppReturnPath();

  return useMemo(
    () => (team == null ? undefined : resolveGlobalNavReturnHref(returnPath, team.projects)),
    [returnPath, team],
  );
};

/** `resolveGlobalNavEnvironment` for the signed-in team; undefined until the team loads. */
export const useGlobalNavEnvironment = (): NavLinkEnvironmentIds | undefined => {
  const { team } = useRouteAndTeamInfo();
  // Read on every render: moving client-side from an environment to a global page can keep this
  // hook's component mounted, and the environment's nav writes the path on the way out.
  const returnPath = getNavigationV2AppReturnPath();

  return useMemo(
    () => (team == null ? undefined : resolveGlobalNavEnvironment(returnPath, team.projects)),
    [returnPath, team],
  );
};

const NavLinkEnvironmentContext = createContext<NavLinkEnvironmentIds | undefined>(undefined);

/**
 * Points the project nav's links into `environment` on a page whose URL names no environment (a
 * global settings page). Only links read it: requests take their environment from the URL, so the
 * nav's queries keep following the route.
 */
export const NavLinkEnvironmentProvider: React.FC<
  PropsWithChildren<{ environment: NavLinkEnvironmentIds | undefined }>
> = ({ environment, children }) => (
  <NavLinkEnvironmentContext.Provider value={environment}>
    {children}
  </NavLinkEnvironmentContext.Provider>
);

/** The environment a `NavLinkEnvironmentProvider` above points the nav into, if any. */
export const useScopedNavLinkEnvironmentId = (): string | undefined =>
  useContext(NavLinkEnvironmentContext)?.environmentId;

/** The environment the project nav links into: the route's own, or the one its page scopes it to. */
export const useNavLinkEnvironment = (): NavLinkEnvironment | undefined => {
  const { projectId, environmentId, environment, teamService } = useRouteAndTeamInfo();
  const scoped = useContext(NavLinkEnvironmentContext);

  return useMemo(() => {
    if (projectId != null && environmentId != null) {
      return { projectId, environmentId, environment };
    }
    if (scoped == null) return undefined;

    return { ...scoped, environment: teamService.environmentById(scoped.environmentId) };
  }, [environment, environmentId, projectId, scoped, teamService]);
};
